import { supabase } from './supabase';
import { TaskCard } from '../types/board';

export interface UserColumnNotification {
  id: string;
  user_id: string;
  column_id: string;
  business_id: string;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface TaskDropdownItem {
  id: string;
  title: string;
  description?: string;
  status: TaskCard['status'];
  urgency: TaskCard['urgency'];
  board_title: string;
  column_title: string;
  scheduled_date?: string;
}

/**
 * Toggle notification preference for a specific column
 */
export async function toggleColumnNotification(
  userId: string,
  columnId: string,
  businessId: string,
  enabled: boolean
): Promise<void> {
  if (!supabase) {
    throw new Error('Supabase client not initialized');
  }

  if (enabled) {
    // Enable notifications - create or update the preference
    const { error } = await supabase
      .from('user_column_notifications')
      .upsert({
        user_id: userId,
        column_id: columnId,
        business_id: businessId,
        enabled: true
      }, {
        onConflict: 'user_id,column_id'
      });

    if (error) {
      console.error('Error enabling column notifications:', error);
      throw new Error('Failed to enable notifications for this list');
    }
  } else {
    // Disable notifications - remove the preference
    const { error } = await supabase
      .from('user_column_notifications')
      .delete()
      .eq('user_id', userId)
      .eq('column_id', columnId);

    if (error) {
      console.error('Error disabling column notifications:', error);
      throw new Error('Failed to disable notifications for this list');
    }
  }
}

/**
 * Check if user has notifications enabled for a specific column
 */
export async function isColumnNotificationEnabled(
  userId: string,
  columnId: string
): Promise<boolean> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return false;
  }

  const { data, error } = await supabase
    .from('user_column_notifications')
    .select('enabled')
    .eq('user_id', userId)
    .eq('column_id', columnId)
    .eq('enabled', true)
    .maybeSingle();

  if (error) {
    console.error('Error checking column notification status:', error);
    return false;
  }

  return !!data;
}

/**
 * Get all tasks from columns that the user has notifications enabled for
 */
export async function getUserNotificationTasks(
  userId: string,
  businessId: string
): Promise<TaskDropdownItem[]> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return [];
  }

  try {
    // First get all columns the user has notifications enabled for
    const { data: notificationColumns, error: notificationError } = await supabase
      .from('user_column_notifications')
      .select(`
        column_id,
        board_columns!inner (
          id,
          title,
          board_id,
          tasks_board!inner (
            id,
            board_title
          )
        )
      `)
      .eq('user_id', userId)
      .eq('business_id', businessId)
      .eq('enabled', true);

    if (notificationError) {
      console.error('Error fetching notification columns:', notificationError);
      return [];
    }

    if (!notificationColumns || notificationColumns.length === 0) {
      return [];
    }

    // Extract column IDs
    const columnIds = notificationColumns.map(nc => nc.column_id);

    // Get all in-progress tasks from these columns
    const { data: tasks, error: tasksError } = await supabase
      .from('task_cards')
      .select(`
        id,
        title,
        description,
        status,
        urgency,
        scheduled_date,
        column_id,
        board_columns!inner (
          id,
          title,
          board_id,
          tasks_board!inner (
            id,
            board_title
          )
        )
      `)
      .in('column_id', columnIds)
      .eq('business_id', businessId)
      .eq('status', 'in-progress')
      .order('created_at', { ascending: false });

    if (tasksError) {
      console.error('Error fetching notification tasks:', tasksError);
      return [];
    }

    // Transform the data to match TaskDropdownItem interface
    return (tasks || []).map(task => ({
      id: task.id,
      title: task.title,
      description: task.description,
      status: task.status as TaskCard['status'],
      urgency: task.urgency as TaskCard['urgency'],
      board_title: (task.board_columns as any).tasks_board.board_title,
      column_title: (task.board_columns as any).title,
      scheduled_date: task.scheduled_date
    }));

  } catch (error) {
    console.error('Error getting user notification tasks:', error);
    return [];
  }
}

/**
 * Get count of pending tasks from user's notification columns
 */
export async function getUserNotificationTasksCount(
  userId: string,
  businessId: string
): Promise<number> {
  if (!supabase) {
    console.error('Supabase client not initialized');
    return 0;
  }

  try {
    // Get column IDs user has notifications for
    const { data: notificationColumns, error: notificationError } = await supabase
      .from('user_column_notifications')
      .select('column_id')
      .eq('user_id', userId)
      .eq('business_id', businessId)
      .eq('enabled', true);

    if (notificationError || !notificationColumns || notificationColumns.length === 0) {
      return 0;
    }

    const columnIds = notificationColumns.map(nc => nc.column_id);

    // Count tasks that are in-progress
    const { count, error: countError } = await supabase
      .from('task_cards')
      .select('id', { count: 'exact' })
      .in('column_id', columnIds)
      .eq('business_id', businessId)
      .eq('status', 'in-progress');

    if (countError) {
      console.error('Error counting notification tasks:', countError);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error getting notification tasks count:', error);
    return 0;
  }
} 